import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  AccountBalance as VATIcon,
  ArrowBack as BackIcon,
  ShoppingCart as PurchaseIcon,
} from '@mui/icons-material';
import ReportDataGrid from '../components/ReportDataGrid';
import reportService from '../services/reportService';
import supplierService from '../services/supplierService';
import { useNavigate } from 'react-router-dom';
import riyalIcon from '../assets/riyal.png';
import { formatDate } from '../utils/dateUtils';
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/apiService';

const VATPurchaseReport = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isVatAdmin = currentUser?.role === 'vatadmin';

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const { t, language } = useTranslation();
  const [filters, setFilters] = useState({
    groupBy: 'day',
    startDate: new Date(),
    endDate: new Date(),
    supplierId: 'all',
    tenantId: 'all',
  });

  const getDateRangeForGroupBy = (groupBy) => {
    const today = new Date();
    let startDate, endDate;

    switch (groupBy) {
      case 'day':
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case 'week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - today.getDay());
        endDate = new Date(today);
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today);
        break;
      case 'year':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today);
        break;
      default:
        startDate = new Date(today);
        endDate = new Date(today);
    }
    return { startDate, endDate };
  };

  const fetchSuppliers = async () => {
    try {
      const response = await supplierService.getSuppliers(1, 0);
      if (response && response.suppliers) {
        setSuppliers(response.suppliers);
      } else if (response.success && response.data) {
        setSuppliers(response.data);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/vat-admin/my-companies', { params: { limit: 'all' } });
      const records = res.data?.data ?? res.data ?? [];
      setCompanies(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const toParamDate = (date) => {
        if (!date) return undefined;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const params = {
        startDate: toParamDate(filters.startDate),
        endDate: toParamDate(filters.endDate),
      };
      if (language === 'ar' || language === 'en') {
        params.language = language;
      }

      if (filters.supplierId && filters.supplierId !== 'all') {
        params.supplierId = filters.supplierId;
      }

      // For vatadmin: pass tenantIds when a specific company is selected
      if (isVatAdmin && filters.tenantId && filters.tenantId !== 'all') {
        params.tenantIds = filters.tenantId;
      }

      const response = await reportService.getVATPurchase(params);

      if (response.success) {
        const flattenedData = [];
        if (response.data) {
          response.data.forEach((periodGroup) => {
            if (periodGroup.invoices && periodGroup.invoices.length > 0) {
              periodGroup.invoices.forEach((invoice) => {
                flattenedData.push({
                  ...invoice,
                  period: periodGroup.period,
                });
              });
            }
          });
        }
        setReportData(flattenedData);
        setTotals(response.totals || null);
      }
    } catch (error) {
      console.error('Error fetching VAT purchase report:', error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
    if (isVatAdmin) {
      fetchCompanies();
    }
    fetchReport();
  }, []);

  const handleFilterChange = (field, value) => {
    if (field === 'groupBy') {
      const { startDate, endDate } = getDateRangeForGroupBy(value);
      setFilters(prev => ({
        ...prev,
        [field]: value,
        startDate,
        endDate
      }));
    } else {
      setFilters(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleApplyFilters = () => {
    fetchReport();
  };

  const columns = [
    {
      field: 'invoiceDate',
      headerName: t('Reports.Date'),
      flex: 0.8,
      minWidth: 110,
      sortable: true,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: 'invoiceNumber',
      headerName: t('Reports.Invoice_No'),
      flex: 0.8,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'supplierName',
      headerName: t('Reports.Supplier'),
      flex: 1.2,
      minWidth: 180,
      sortable: true,
      renderCell: (params) => params.row.supplierId === null ? t('Reports.Walk_In_Supplier') : (params.value || '-'),
    },
    {
      field: 'subTotal',
      headerName: t('Reports.Amount'),
      flex: 0.9,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%' }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>
        </Box>
      ),
    },
    {
      field: 'vatAmount',
      headerName: t('Reports.VAT_Amount'),
      flex: 0.9,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%' }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>
        </Box>
      ),
    },
    {
      field: 'totalAmount',
      headerName: t('Reports.Total'),
      flex: 1,
      minWidth: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Box className="font-weight-bold" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%' }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>
        </Box>
      ),
    },
  ];

  const summaryCards = [
    {
      title: t('Reports.Total_Invoices'),
      value: totals?.totalInvoices || reportData.length || 0,
      icon: <ReceiptIcon />,
      color: '#4361ee',
      format: (val) => val.toLocaleString(),
    },
    {
      title: t('Reports.Total_Purchases_Amount'),
      value: totals?.grandTotal || reportData.reduce((sum, item) => sum + (item.totalAmount || 0), 0) || 0,
      icon: <PurchaseIcon />,
      color: '#2e7d32',
      format: (val) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </Box>
      ),
    },
    {
      title: t('Reports.Total_VAT_Paid'),
      value: totals?.totalVAT || reportData.reduce((sum, item) => sum + (item.vatAmount || 0), 0) || 0,
      icon: <VATIcon />,
      color: '#ed6c02',
      format: (val) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </Box>
      ),
    },
  ];

  const handleBack = () => {
    navigate('/reports');
  };

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={handleBack} sx={{ mr: 1.5, bgcolor: 'rgba(67, 97, 238, 0.1)' }}>
          <BackIcon sx={{ transform: language === 'ar' ? 'rotate(180deg)' : 'none' }} />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight="700" gutterBottom>
            {t('Reports.VAT_Purchase_Report')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('Reports.VAT_Purchase_Report_Description')}
          </Typography>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 1, mb: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 0 }}>
        <Typography variant="caption" fontWeight="600" sx={{ mb: 1, display: 'block', fontSize: '0.75rem' }}>
          {t('Reports.Filters')}
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={1} sx={{ mt: 0 }}>
            {/* Group By */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                select
                label={t('Reports.Group_By')}
                value={filters.groupBy}
                onChange={(e) => handleFilterChange('groupBy', e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 0, height: '36px', fontSize: '0.875rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                  '& .MuiOutlinedInput-input': { padding: '8px 14px' },
                }}
              >
                <MenuItem value="day">{t('Reports.Daily')}</MenuItem>
                <MenuItem value="week">{t('Reports.Weekly')}</MenuItem>
                <MenuItem value="month">{t('Reports.Monthly')}</MenuItem>
                <MenuItem value="year">{t('Reports.Yearly')}</MenuItem>
              </TextField>
            </Grid>

            {/* Supplier */}
            <Grid item xs={12} sm={6} md={isVatAdmin ? 2 : 3}>
              <TextField
                fullWidth
                select
                label={t('Reports.Supplier')}
                value={filters.supplierId}
                onChange={(e) => handleFilterChange('supplierId', e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 0, height: '36px', fontSize: '0.875rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                  '& .MuiOutlinedInput-input': { padding: '8px 14px' },
                }}
              >
                <MenuItem value="all">{t('Reports.All_Suppliers')}</MenuItem>
                {suppliers.map((sup) => (
                  <MenuItem key={sup._id} value={sup._id}>
                    {sup.supplierName || sup.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Start Date */}
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label={t('Reports.Start_Date')}
                value={filters.startDate}
                onChange={(date) => handleFilterChange('startDate', date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': { borderRadius: 0, height: '36px', fontSize: '0.875rem' },
                      '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                      '& .MuiOutlinedInput-input': { padding: '8px 14px' },
                    },
                  },
                }}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label={t('Reports.End_Date')}
                value={filters.endDate}
                onChange={(date) => handleFilterChange('endDate', date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': { borderRadius: 0, height: '36px', fontSize: '0.875rem' },
                      '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                      '& .MuiOutlinedInput-input': { padding: '8px 14px' },
                    },
                  },
                }}
              />
            </Grid>

            {/* Company dropdown — vatadmin only */}
            {isVatAdmin && (
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  select
                  label={t('Common.Company_Name') || 'Company'}
                  value={filters.tenantId}
                  onChange={(e) => handleFilterChange('tenantId', e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 0, height: '36px', fontSize: '0.875rem' },
                    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                    '& .MuiOutlinedInput-input': { padding: '8px 14px' },
                  }}
                >
                  <MenuItem value="all">
                    {t('Common.All_Companies') || 'All Companies'}
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.tenantId} value={company.tenantId}>
                      {company.name || company.tenantId}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {/* Apply Filters */}
            <Grid item xs={12} sm={6} md={isVatAdmin ? 2 : 3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyFilters}
                disabled={loading}
                sx={{ borderRadius: 0, height: '36px', textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }}
              >
                {loading ? <CircularProgress size={20} /> : t('Reports.Apply_Filters')}
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      {totals && (
        <Grid container spacing={1} sx={{ mb: 0.5 }}>
          {summaryCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0,
                  borderLeft: `4px solid ${card.color}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '8px',
                      bgcolor: `${card.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1.5,
                    }}
                  >
                    {React.cloneElement(card.icon, { sx: { color: card.color, fontSize: 20 } })}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {card.title}
                    </Typography>
                    <Typography variant="h6" fontWeight="700">
                      {card.format(card.value)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <ReportDataGrid
        title={t('Reports.VAT_Purchase_Report')}
        data={reportData}
        columns={columns}
        loading={loading}
        onRefresh={fetchReport}
        exportFileName={`vat-purchase-report-${new Date().toISOString().split('T')[0]}`}
        pageSize={25}
        height={700}
        getRowId={(row) => row.invoiceNumber || row.id || Math.random().toString()}
        totals={totals}
        dateRange={{ startDate: filters.startDate, endDate: filters.endDate }}
      />
    </Container>
  );
};

export default VATPurchaseReport;
